import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Account } from "../Account/accountApi"
import { faFireFlameCurved, faQuestionCircle, faGear, faTrash, faInfo, faDatabase, faFileContract, faClipboard, faBoxDollar, } from '@fortawesome/pro-solid-svg-icons'
import { faArchive, faCalendar, faCreditCard, faHouseChimney, faKey, faSignal, faStopwatch, faToolbox, faUser, faUserClock, faPuzzlePiece, faCity, faLeaf, faRocket, faPalletBox, faRoute, faChartPie } from "@fortawesome/pro-duotone-svg-icons"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { PropsWithChildren } from "react"

const IconBlank = () => <span style={{ width: 14, height: 14, marginRight: 10 }}>&nbsp;</span>

const MenuIcon = ({ icon }: { icon: IconProp }) => (
  <FontAwesomeIcon
    icon={icon}
    style={{
      marginLeft: 12,
      marginRight: 10,
      fontSize: 14
    }}
  />
)

const HeaderLabel = ({ children }: PropsWithChildren) => (
  <span style={{
    fontWeight: 'normal',
    textTransform: 'uppercase',
    fontSize: 11.5,
    color: 'rgba(255, 255, 255, 0.5)',
    marginLeft: 12
  }}>
    {children}
  </span>
)

export type MenuItemType = {
  key: string
  label: string | JSX.Element
  icon: JSX.Element
  path?: string
  url?: string
  children?: MenuItemType[]
}

export default (currentUser: Account | undefined, collapsed: boolean, showPaymentsLink: boolean, showServiceTickets: boolean) => {
  const roles = currentUser?.roles ?? []
  const hasAdminRole = roles.includes('Administrator')
  const hasAccountingRole = roles.includes('Accounting')
  const hasInvoicingPermission = currentUser?.permissionKeys.includes('allow_invoicing') ?? false
  const isSuperUser = currentUser?.isSuperUser ?? false

  return [
    {
      key: 'dashboard',
      label: currentUser?.tenant?.name ?? 'Dashboard',
      icon: collapsed ? <FontAwesomeIcon icon={faLeaf} /> : <MenuIcon icon={faLeaf} />,
      path: '/',
    },
    {
      key: 'help',
      label: <HeaderLabel>Help</HeaderLabel>,
      icon: collapsed ? <FontAwesomeIcon icon={faQuestionCircle} /> : <IconBlank />,
      url: 'https://help.synkedup.com/en',
    },
    {
      key: 'information',
      label: <HeaderLabel>Information</HeaderLabel>,
      icon: collapsed ? <FontAwesomeIcon icon={faInfo} /> : <IconBlank />,
      children: [
        {
          key: 'clients-and-properties',
          label: 'Clients & Properties',
          icon: <MenuIcon icon={faHouseChimney} />,
          path: '/customers',
        },
        {
          key: 'consultations',
          label: 'Consultations',
          icon: <MenuIcon icon={faUserClock} />,
          path: '/consultations',
        },
        {
          key: 'estimates',
          label: 'Estimates',
          icon: <MenuIcon icon={faChartPie} />,
          path: '/estimates'
        },
        {
          key: 'projects',
          label: 'Projects',
          icon: <MenuIcon icon={faArchive} />,
          path: '/projects',
        },
        ...(showServiceTickets ? [{
          key: 'service-tickets',
          label: 'Service Tickets',
          icon: <MenuIcon icon={faFireFlameCurved} />,
          path: '/tickets',
        }] : []),
        {
          key: 'maintenance',
          label: 'Maintenance',
          icon: <MenuIcon icon={faToolbox} />,
          path: '/maintenance',
        },
        {
          key: 'scheduling',
          label: 'Scheduling',
          icon: <MenuIcon icon={faCalendar} />,
          path: '/scheduling',
        },
        {
          key: 'time',
          label: 'Time',
          icon: <MenuIcon icon={faStopwatch} />,
          path: '/time',
        },
        ...(hasAccountingRole && hasInvoicingPermission ? [{
          key: 'invoicing',
          label: 'Invoicing',
          icon: <MenuIcon icon={faCreditCard} />,
          path: '/billing',
        }] : []),
        ...(showPaymentsLink ? [{
          key: 'payments',
          label: 'Payments',
          icon: <MenuIcon icon={faBoxDollar} />,
          path: '/payments',
        }] : []),
        {
          key: 'datacenter',
          label: 'Datacenter',
          icon: <MenuIcon icon={faDatabase} />,
          path: '/datacenter',
        },
      ]
    },
    {
      key: 'setup',
      label: <HeaderLabel>Setup</HeaderLabel>,
      icon: collapsed ? <FontAwesomeIcon icon={faGear} /> : <IconBlank />,
      children: [
        {
          key: 'item-catalog',
          label: 'Item Catalog',
          icon: <MenuIcon icon={faRocket} />,
          path: '/items'
        },
        {
          key: 'vendors',
          label: 'Vendors',
          icon: <MenuIcon icon={faPalletBox} />,
          path: '/vendors'
        },
        {
          key: 'routing',
          label: 'Routing',
          icon: <MenuIcon icon={faClipboard} />,
          path: '/routing',
        },
        {
          key: 'templates',
          label: 'Templates',
          icon: <MenuIcon icon={faPuzzlePiece} />,
          path: '/items/templates'
        },
        ...(hasAdminRole ? [
          {
            key: 'users',
            label: 'Users',
            icon: <MenuIcon icon={faUser} />,
            path: '/users'
          },
          {
            key: 'my-company',
            label: 'My Company',
            icon: <MenuIcon icon={faCity} />,
            path: '/mycompany'
          },
          {
            key: 'proposal-settings',
            label: 'Proposal Settings',
            icon: <MenuIcon icon={faFileContract} />,
            path: '/proposal-settings'
          },
          {
            key: 'invoicing-settings',
            label: 'Invoicing Settings',
            icon: <MenuIcon icon={faCreditCard} />,
            path: '/invoicing-settings'
          },
          {
            key: 'route-settings',
            label: 'Route Settings',
            icon: <MenuIcon icon={faRoute} />,
            path: '/route-settings'
          },
          {
            key: 'system-setup',
            label: 'System Setup',
            icon: <MenuIcon icon={faSignal} />,
            path: '/setup'
          }
        ] : []),
        ...(isSuperUser ? [
          {
            key: 'tenants',
            label: 'Tenants',
            icon: <MenuIcon icon={faCity} />,
            path: '/tenants'
          },
          {
            key: 'admin-console',
            label: 'Admin Console',
            icon: <MenuIcon icon={faKey} />,
            path: '/admin-app'
          },
        ] : [])
      ]
    },
    {
      key: 'recycle-bin',
      label: <HeaderLabel>Recycle Bin</HeaderLabel>,
      icon: collapsed ? <FontAwesomeIcon icon={faTrash} /> : <IconBlank />,
      children: [
        {
          key: 'deleted-clients',
          label: 'Clients',
          path: '/customers/deleted',
          icon: <MenuIcon icon={faHouseChimney} />
        },
        {
          key: 'deleted-properties',
          label: 'Properties',
          path: '/properties/deleted',
          icon: <MenuIcon icon={faHouseChimney} />
        },
        {
          key: 'deleted-estimates',
          label: 'Estimates',
          path: '/estimates/deleted',
          icon: <MenuIcon icon={faChartPie} />
        },
        {
          key: 'deleted-projects',
          label: 'Projects',
          path: '/projects/deleted',
          icon: <MenuIcon icon={faArchive} />
        },
        ...(showServiceTickets ? [{
          key: 'deleted-service-tickets',
          label: 'Service Tickets',
          path: '/tickets/deleted',
          icon: <MenuIcon icon={faFireFlameCurved} />
        }] : []),
        {
          key: 'deleted-maintenance',
          label: 'Maintenance',
          path: '/maintenance/deleted',
          icon: <MenuIcon icon={faToolbox} />
        },
        {
          key: 'deleted-time',
          label: 'Time',
          path: '/time/deleted',
          icon: <MenuIcon icon={faStopwatch} />
        },
        {
          key: 'deleted-item-catalog',
          label: 'Item Catalog',
          path: '/items/deleted',
          icon: <MenuIcon icon={faRocket} />
        },
        {
          key: 'deleted-templates',
          label: 'Templates',
          path: '/items/templates/deleted',
          icon: <MenuIcon icon={faPuzzlePiece} />
        },
        {
          key: 'deleted-users',
          label: 'Users',
          path: '/users/deleted',
          icon: <MenuIcon icon={faUser} />
        }
      ]
    }
  ]
}

export const findItemByKeyPath = (items: MenuItemType[], keyPath: string[]): MenuItemType | undefined => {
  const key = keyPath[keyPath.length - 1]
  const item = items.find((x: any) => x.key === key)
  if (item?.children) {
    return findItemByKeyPath(item.children, keyPath.slice(0, -1))
  }
  return item
}

const additionalPathKeyMapping: Record<string, string> = {
  '/project-estimate': 'estimates',
  '/ticket-estimate': 'estimates',
  '/maintenance-estimate': 'estimates'
}

export const findItemKeyByPath = (items: MenuItemType[], path: string): MenuItemType | undefined => {
  if (additionalPathKeyMapping[path]) {
    return items.flatMap(x => x.children ?? []).find(x => x.key === additionalPathKeyMapping[path])
  }

  const allItems = items.reduce((acc: MenuItemType[], item: MenuItemType) => [
    ...acc,
    item,
    ...(item.children ?? [])
  ], [])

  return allItems.find(x => x.path === path)
}
